import { useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Settings → Tags management (docs/16 §H). CRUD over the existing tags table
// (engine already uses tags + taggables). Nothing here changes how tags are
// applied — this is the admin face only. Auto-tag rules are out of scope (future).

const SCOPES = ["lead", "contact", "buyer", "property"] as const;
const UNCATEGORIZED = "Uncategorized";
// A small default palette; the color field is free-form so any hex works.
const SWATCHES = ["#64748b", "#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6"];

type Tag = {
  id: string;
  name: string;
  category: string | null;
  color: string | null;
  scope: string | null;
};

const TagsPage = () => {
  const notify = useNotify();
  const [tags, setTags] = useState<Tag[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [nName, setNName] = useState("");
  const [nCat, setNCat] = useState("");
  const [nColor, setNColor] = useState(SWATCHES[0]);
  const [nScope, setNScope] = useState<string>("lead");

  const load = async () => {
    const sb = getSupabaseClient();
    const [{ data: t }, { data: tg }] = await Promise.all([
      sb.from("tags").select("*").order("category", { nullsFirst: true }).order("name"),
      sb.from("taggables").select("tag_id"),
    ]);
    setTags((t as Tag[]) ?? []);
    const c: Record<string, number> = {};
    (tg ?? []).forEach((r: any) => (c[r.tag_id] = (c[r.tag_id] ?? 0) + 1));
    setCounts(c);
  };
  useEffect(() => { load(); }, []);

  const patch = async (id: string, p: Partial<Tag>) => {
    const { error } = await getSupabaseClient().from("tags").update(p).eq("id", id);
    if (error) notify(`Failed: ${error.message}`, { type: "error" }); else load();
  };
  const add = async () => {
    if (!nName.trim()) return;
    const { error } = await getSupabaseClient().from("tags").insert({
      name: nName.trim(),
      category: nCat.trim() || null,
      color: nColor || null,
      scope: nScope || null,
    });
    if (error) { notify(`Failed: ${error.message}`, { type: "error" }); return; }
    setNName(""); setNCat(""); setNColor(SWATCHES[0]); setNScope("lead");
    notify("Tag created", { type: "success" });
    load();
  };
  const del = async (t: Tag) => {
    const used = counts[t.id] ?? 0;
    if (!window.confirm(`Delete tag "${t.name}"?${used ? ` It is applied to ${used} record(s); those tag links are removed too.` : ""}`)) return;
    const { error } = await getSupabaseClient().from("tags").delete().eq("id", t.id);
    if (error) notify(`Failed: ${error.message}`, { type: "error" }); else { notify("Tag deleted", { type: "success" }); load(); }
  };

  // group by category for the list
  const groups: Record<string, Tag[]> = {};
  tags.forEach((t) => {
    const k = t.category?.trim() || UNCATEGORIZED;
    (groups[k] ??= []).push(t);
  });
  const groupNames = Object.keys(groups).sort((a, b) =>
    a === UNCATEGORIZED ? 1 : b === UNCATEGORIZED ? -1 : a.localeCompare(b));

  return (
    <div className="p-2 max-w-3xl">
      <h1 className="text-xl font-semibold">Tags</h1>
      <div className="text-sm text-muted-foreground mb-4">
        Create and organize tags (name, category, color, scope). Tags are data — changes apply immediately.
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Add a tag</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-2">
          <Field label="Name"><Input className="w-40" value={nName} placeholder="e.g. Motivated" onChange={(e) => setNName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} /></Field>
          <Field label="Category"><Input className="w-40" value={nCat} placeholder="e.g. Motivation" onChange={(e) => setNCat(e.target.value)} /></Field>
          <Field label="Color">
            <div className="flex items-center gap-1">
              <input type="color" className="h-9 w-10 rounded border bg-background p-0.5" value={nColor} onChange={(e) => setNColor(e.target.value)} />
              {SWATCHES.map((s) => (
                <button key={s} type="button" title={s} onClick={() => setNColor(s)} className="h-5 w-5 rounded-full border" style={{ backgroundColor: s }} />
              ))}
            </div>
          </Field>
          <Field label="Scope">
            <select className="border rounded px-2 py-2 text-sm bg-background" value={nScope} onChange={(e) => setNScope(e.target.value)}>
              {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Button onClick={add}>Add tag</Button>
        </CardContent>
      </Card>

      {groupNames.map((g) => (
        <Card key={g} className="mt-4">
          <CardHeader><CardTitle className="text-base">{g} <span className="text-xs font-normal text-muted-foreground">({groups[g].length})</span></CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {groups[g].map((t) => (
              <div key={t.id} className="flex flex-wrap items-center gap-2 border-b pb-2 last:border-0 last:pb-0">
                <span className="inline-block h-4 w-4 rounded-full border shrink-0" style={{ backgroundColor: t.color ?? "transparent" }} />
                <Input className="w-40" defaultValue={t.name} onBlur={(e) => e.target.value.trim() && e.target.value !== t.name && patch(t.id, { name: e.target.value.trim() })} />
                <Input className="w-36" placeholder="category" defaultValue={t.category ?? ""} onBlur={(e) => (e.target.value.trim() || null) !== t.category && patch(t.id, { category: e.target.value.trim() || null })} />
                <input type="color" className="h-9 w-10 rounded border bg-background p-0.5" value={t.color ?? "#64748b"} onChange={(e) => patch(t.id, { color: e.target.value })} />
                <select className="border rounded px-2 py-2 text-sm bg-background" value={t.scope ?? "lead"} onChange={(e) => patch(t.id, { scope: e.target.value })}>
                  {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <Badge variant="outline" className="ml-auto">{counts[t.id] ?? 0} use{(counts[t.id] ?? 0) === 1 ? "" : "s"}</Badge>
                <Button variant="ghost" size="sm" onClick={() => del(t)}>Delete</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
      {tags.length === 0 && <div className="text-sm text-muted-foreground mt-4">No tags yet — add one above.</div>}
    </div>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs text-muted-foreground">{label}</span>
    {children}
  </div>
);

TagsPage.path = "/settings/tags";
export default TagsPage;
